import { HTMLAttributes, ReactNode } from "react";
import { formatLocalizedNumber } from "@common/helpers/i18n";
import { useDocumentDirection } from "@app/hooks";
import { cn } from "@app/lib/utils";
import { ActionGroup } from "./ActionGroup";
import {
  Card,
  CardContent,
  CardIcon,
  cardInteractiveClassName,
  cardSurfaceClassName,
} from "./Card";

export const pageMaxWidthClassName = "max-w-[1440px]";
export const pageStackGapClassName = "gap-4 md:gap-5";
export const pageGridClassName =
  "grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 xl:grid-cols-3 2xl:grid-cols-4";

export function Page({
  children,
  className,
  wide,
}: {
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div
      className={cn(
        "mx-auto flex w-full min-w-0 flex-col px-0 pb-4",
        pageStackGapClassName,
        wide ? "max-w-none" : pageMaxWidthClassName,
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageIntro({
  title,
  description,
  action,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("py-0.5 md:py-1", className)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-extrabold leading-7 text-foreground md:text-xl md:leading-8">
            {title}
          </h2>
          {description && (
            <p className="mt-0.5 max-w-3xl text-xs font-medium leading-4 text-muted-foreground md:text-sm md:leading-5">
              {description}
            </p>
          )}
        </div>
        {action && (
          <ActionGroup className="shrink-0" align="end" gap="default">
            {action}
          </ActionGroup>
        )}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </section>
  );
}

export function PageToolbar({
  children,
  className,
  surface = "plain",
}: {
  children: ReactNode;
  className?: string;
  surface?: "plain" | "card";
}) {
  const direction = useDocumentDirection();

  return (
    <div
      data-page-toolbar="true"
      dir={direction}
      className={cn(
        "flex w-full min-w-0 flex-nowrap items-center gap-2 overflow-x-auto overflow-y-visible pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:gap-2.5 sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden",
        "justify-start",
        surface === "card" &&
          "rounded-lg border border-border bg-card/80 p-2 shadow-soft",
        "[&>*]:max-w-full [&>a]:flex-none [&>button]:flex-none [&>a]:w-auto [&>button]:w-auto",
        "[&>*:not(a):not(button)]:w-[min(76vw,20rem)] [&>*:not(a):not(button)]:min-w-[176px] sm:[&>*:not(a):not(button)]:w-[208px] sm:[&>*:not(a):not(button)]:flex-none",
        "[&_a]:shadow-none [&_button]:shadow-none",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageControls({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const direction = useDocumentDirection();

  return (
    <div
      data-page-controls="true"
      dir={direction}
      className={cn(
        "flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto overflow-y-visible pb-1 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:gap-2.5 sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageActions({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <ActionGroup
      align="end"
      className={cn("w-full sm:w-auto", className)}
      gap="default"
      mobile="row"
      stretch={false}
    >
      {children}
    </ActionGroup>
  );
}

export function PageGrid({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("grid", pageGridClassName, className)} {...props}>
      {children}
    </div>
  );
}

export function ActionCard({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <article
      className={cn(
        cardSurfaceClassName,
        cardInteractiveClassName,
        "h-full p-4 md:p-5",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {icon && <CardIcon>{icon}</CardIcon>}
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold leading-6 text-foreground md:text-base">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-xs leading-4 text-muted-foreground md:text-sm md:leading-5">
              {description}
            </p>
          )}
        </div>
      </div>
      {action && <div className="mt-4 md:mt-5">{action}</div>}
    </article>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold leading-5 text-muted-foreground md:text-sm">
              {label}
            </p>
            <div className="mt-2 text-xl font-extrabold leading-7 text-foreground md:mt-3 md:text-2xl md:leading-8">
              {typeof value === "number" ? formatLocalizedNumber(value) : value}
            </div>
            {hint && (
              <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
                {hint}
              </p>
            )}
          </div>
          {icon && <CardIcon className="h-10 w-10">{icon}</CardIcon>}
        </div>
      </CardContent>
    </Card>
  );
}
